
    import { defineComponent } from 'vue';
    import dayjs from 'dayjs';
    import { IonRippleEffect, IonSearchbar } from '@ionic/vue';
    import BaseLoader from '@/components/BaseLoader.vue';
    import CardModal from '@/components/CardModal.vue';
    import OrderService from '@/services/order.service';
    import ToastService from '@/services/toast.service';

    type Field = 'replaced_at' | 'serial_number' | 'article' | 'comment';

    interface Product {
        id: number;
        name: string;
        sku: string;
    }

    interface Card {
        key: number;
        id: number | null;
        open: boolean;
        // Registered before the product list: its free text is kept until a product is picked.
        legacy: boolean;
        form: { replaced_at: string; serial_number: string; product_id: number | null; article: string; article_number: string | null; comment: string };
        errors: Partial<Record<Field, string>>;
    }

    const MAX_OPTIONS = 50;

    let nextKey = 0;

    const newCard = (part: any = null): Card => ({
        key   : ++nextKey,
        id    : part ? part.id : null,
        open    : !part,
        legacy  : !!part && !part.product_id,
        form    : {
            replaced_at   : part ? part.replaced_at : dayjs().format('YYYY-MM-DD'),
            serial_number : part ? part.serial_number : '',
            product_id    : part?.product_id ?? null,
            article       : part ? part.article : '',
            article_number: part?.article_number ?? null,
            comment       : part?.comment ?? '',
        },
        errors: {},
    });

    /**
     * The defective parts a mechanic replaced during an appointment, as a
     * repeater of cards. Nothing is saved per card: the order form asks for
     * validate() and then send() when the mechanic moves on, and everything that
     * is filled in goes along. Cards that were registered earlier come back
     * collapsed; opening one lets the mechanic change it.
     *
     * The article is picked from the same products as "product toevoegen", in a
     * modal with a search bar, so every mechanic names a part the same way.
     */
    export default defineComponent({
        name      : 'DefectivePartsSection',
        components: { BaseLoader, CardModal, IonRippleEffect, IonSearchbar },
        props     : {
            appointmentId: { type: Number, required: true },
            editable     : { type: Boolean, default: true },
            // "Ja" on the order form. With "Nee" the cards stay as they are, but nothing is sent.
            active       : { type: Boolean, default: true },
            // The products a mechanic can add to an order, to pick the defective article from.
            products     : { type: Array as () => Product[], default: () => [] },
        },
        emits     : ['loaded'],
        data() {
            return {
                cards       : [] as Card[],
                isLoading   : true,
                registered  : 0,
                loadFailed  : false,
                warning     : null as string | null,
                warningKey  : null as number | null,
                today       : dayjs().format('YYYY-MM-DD'),
                // The card whose article is being picked in the modal, and what is typed there.
                pickingCard : null as Card | null,
                search      : '',
            };
        },
        computed: {
            /**
             * The products whose name or article number contains what is typed,
             * capped so the list stays quick to scroll on a phone.
             */
            allMatches(): Product[] {
                const query = this.search.trim().toLowerCase();
                const products = this.products as Product[];

                return query ? products.filter((product) => product.name.toLowerCase().includes(query) || String(product.sku).toLowerCase().includes(query)) : products;
            },
            matches(): Product[] {
                return this.allMatches.slice(0, MAX_OPTIONS);
            },
            hasMoreMatches(): boolean {
                return this.allMatches.length > MAX_OPTIONS;
            },
        },
        updated() {
            this.growTextareas();
        },
        async mounted() {
            await this.loadRegistered();

            if (this.editable && this.cards.length === 0) {
                this.cards.push(newCard());
            }
            this.isLoading = false;
            this.$emit('loaded', this.registered);
        },
        methods: {
            /**
             * Loads the registered parts and adds the ones that are not shown yet.
             * Sending replaces the list on the server, so it must not happen on a
             * list that failed to load: that would remove what is registered.
             */
            async loadRegistered(): Promise<boolean> {
                try {
                    const parts = await OrderService.getDefectiveParts(this.appointmentId);
                    const shown = this.cards.map((card) => card.id);

                    this.cards = [...parts.filter((part: any) => !shown.includes(part.id)).map((part: any) => newCard(part)), ...this.cards];
                    this.registered = parts.length;
                    this.loadFailed = false;

                    return true;
                } catch (error: any) {
                    this.loadFailed = true;

                    // With a response, the order service already toasted what went wrong.
                    if (!error?.response) {
                        await ToastService.presentToast('De defecte onderdelen konden niet worden opgehaald', 2500, 'danger');
                    }

                    return false;
                }
            },

            /**
             * A comment grows with its text instead of scrolling or being resized by
             * hand: the height follows the content, with the three rows as a minimum.
             */
            growTextareas() {
                (this.$el as HTMLElement).querySelectorAll('textarea').forEach((textarea) => {
                    textarea.style.height = 'auto';
                    textarea.style.height = `${textarea.scrollHeight + (textarea.offsetHeight - textarea.clientHeight)}px`;
                });
            },

            /**
             * A card counts once something is filled in. The date alone does not:
             * a new card always starts with today.
             */
            isFilled(card: Card) {
                return card.id !== null || !!(card.form.serial_number.trim() || card.form.article.trim() || card.form.comment.trim());
            },

            /**
             * The name of the part, or its serial number while the name is not
             * known yet. It follows what is being typed.
             */
            titleOf(card: Card) {
                return card.form.article.trim() || card.form.serial_number.trim() || 'Nieuw onderdeel';
            },

            summaryOf(card: Card) {
                return [new Date(card.form.replaced_at).toLocaleDateString('nl-NL'), card.form.serial_number.trim()].filter(Boolean).join(' - ');
            },

            openPicker(card: Card) {
                this.pickingCard = card;
                this.search = '';
                (this.$refs.articleModal as any).isOpen = true;
            },

            pick(product: Product) {
                const card = this.pickingCard as Card;

                card.form.product_id = product.id;
                card.form.article = product.name;
                card.form.article_number = product.sku;
                card.legacy = false;
                this.touch(card, 'article');

                (this.$refs.articleModal as any).isOpen = false;
                this.pickingCard = null;
            },

            clearArticle(card: Card) {
                card.form.product_id = null;
                card.form.article = '';
                card.form.article_number = null;
                card.legacy = false;
                this.touch(card, 'article');
            },

            add() {
                this.cards.push(newCard());
                this.clearWarning();
            },

            remove(card: Card) {
                this.cards = this.cards.filter((item) => item !== card);
                this.clearWarning();
            },

            touch(card: Card, field: Field) {
                delete card.errors[field];
                this.clearWarning();
            },

            clearWarning() {
                this.warning = null;
                this.warningKey = null;
            },

            warn(message: string, card: Card | null): string {
                this.warning = message;
                this.warningKey = card ? card.key : null;
                this.$nextTick(() => {
                    document.getElementById(card ? `defective-card-${card.key}` : '')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                });

                return message;
            },

            /**
             * A picked product, or the free text of a part from before the product list.
             */
            hasArticle(card: Card): boolean {
                return card.form.product_id !== null || (card.legacy && !!card.form.article.trim());
            },

            /**
             * Marks the required fields of a card that are still empty.
             */
            validateCard(card: Card): boolean {
                const { replaced_at: replacedAt, serial_number: serialNumber, comment } = card.form;

                card.errors = {
                    ...(replacedAt ? {} : { replaced_at: 'Datum is verplicht' }),
                    ...(serialNumber.trim() ? {} : { serial_number: 'Serienummer is verplicht' }),
                    ...(this.hasArticle(card) ? {} : { article: 'Defecte artikel is verplicht' }),
                    ...(comment.trim() ? {} : { comment: 'Storing of foutcode is verplicht' }),
                };

                return Object.keys(card.errors).length === 0;
            },

            /**
             * Called by the order form before it moves on: returns what is still
             * wrong (shown, and scrolled into view), or null when it can go on.
             */
            validate(): string | null {
                if (!this.active) {
                    return null;
                }

                const filled = this.cards.filter((card) => this.isFilled(card));

                // Removing every registered part is a choice, so it can go on with nothing left.
                if (filled.length === 0 && this.registered === 0) {
                    return this.warn('Je koos "Ja", maar er is nog geen onderdeel ingevuld. Vul een onderdeel in, of kies "Nee".', this.cards[0] ?? null);
                }

                const incomplete = filled.filter((card) => !this.validateCard(card));

                if (incomplete.length > 0) {
                    incomplete.forEach((card) => {
                        card.open = true;
                    });

                    return this.warn('Vul de verplichte velden in.', incomplete[0]);
                }

                this.clearWarning();

                return null;
            },

            /**
             * Sends everything that is filled in, and takes over what the API
             * stored. With "Nee" nothing is sent (the cards are kept, in case the
             * mechanic switches back). Returns false when sending failed.
             */
            async send(): Promise<boolean> {
                if (!this.active) {
                    return true;
                }

                if (this.loadFailed && !(await this.loadRegistered())) {
                    return false;
                }

                const parts = this.cards.filter((card) => this.isFilled(card)).map((card) => ({
                    id           : card.id,
                    replaced_at  : card.form.replaced_at,
                    serial_number: card.form.serial_number.trim(),
                    product_id   : card.form.product_id,
                    article      : card.form.article.trim(),
                    comment      : card.form.comment.trim(),
                }));

                if (parts.length === 0 && this.registered === 0) {
                    return true;
                }

                try {
                    const stored = await OrderService.syncDefectiveParts(this.appointmentId, parts);

                    this.cards = stored.map((part: any) => newCard(part));
                    this.registered = stored.length;

                    return true;
                } catch (error: any) {
                    if (!error?.response) {
                        await ToastService.presentToast('Geen verbinding, de defecte onderdelen zijn niet verstuurd', 2500, 'danger');
                    }

                    return false;
                }
            },
        },
    });
